import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DivisionRoleService } from '../services/division-role-service';
import httpClient from '../services/http-client';
import { 
  DivisionRole,
  DivisionRoleListResponse,
  CreateDivisionRoleRequest,
  UpdateDivisionRoleRequest
} from '../types';

// Create a division role service instance using the default HTTP client
const divisionRoleService = new DivisionRoleService(httpClient);

// Query keys
export const divisionRoleQueryKeys = {
  all: ['divisionRoles'] as const,
  lists: () => [...divisionRoleQueryKeys.all, 'list'] as const,
  list: (divisionId: string) => [...divisionRoleQueryKeys.lists(), divisionId] as const,
  details: () => [...divisionRoleQueryKeys.all, 'detail'] as const,
  detail: (divisionId: string, roleId: string) => [...divisionRoleQueryKeys.details(), divisionId, roleId] as const,
};

/**
 * Hook to get all roles for a specific division
 */
export function useDivisionRoles(divisionId: string, enabled = true) {
  return useQuery({
    queryKey: divisionRoleQueryKeys.list(divisionId),
    queryFn: () => divisionRoleService.getDivisionRoles(divisionId),
    enabled: enabled && !!divisionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get a specific division role by ID
 */
export function useDivisionRole(divisionId: string, roleId: string, enabled = true) {
  return useQuery({
    queryKey: divisionRoleQueryKeys.detail(divisionId, roleId),
    queryFn: () => divisionRoleService.getDivisionRole(divisionId, roleId),
    enabled: enabled && !!divisionId && !!roleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new division role
 */
export function useCreateDivisionRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      divisionId, 
      request 
    }: { 
      divisionId: string; 
      request: CreateDivisionRoleRequest; 
    }) => divisionRoleService.createDivisionRole(divisionId, request),
    onSuccess: (newRole, { divisionId }) => {
      // Invalidate and refetch division roles list
      queryClient.invalidateQueries({ 
        queryKey: divisionRoleQueryKeys.list(divisionId) 
      });
      
      // Add the new role to the cache
      queryClient.setQueryData(
        divisionRoleQueryKeys.detail(divisionId, newRole.id),
        newRole
      );
    },
  });
}

/**
 * Hook to update an existing division role
 */
export function useUpdateDivisionRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      divisionId, 
      roleId, 
      request 
    }: { 
      divisionId: string; 
      roleId: string; 
      request: UpdateDivisionRoleRequest; 
    }) => divisionRoleService.updateDivisionRole(divisionId, roleId, request),
    onSuccess: (updatedRole, { divisionId }) => {
      // Invalidate and refetch division roles list
      queryClient.invalidateQueries({ 
        queryKey: divisionRoleQueryKeys.list(divisionId) 
      });
      
      // Update the specific role in cache
      queryClient.setQueryData(
        divisionRoleQueryKeys.detail(divisionId, updatedRole.id),
        updatedRole
      );
    },
  });
}

/**
 * Hook to delete a division role
 */
export function useDeleteDivisionRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      divisionId, 
      roleId 
    }: { 
      divisionId: string; 
      roleId: string; 
    }) => divisionRoleService.deleteDivisionRole(divisionId, roleId),
    onSuccess: (_, { divisionId, roleId }) => {
      // Invalidate and refetch division roles list
      queryClient.invalidateQueries({ 
        queryKey: divisionRoleQueryKeys.list(divisionId) 
      });
      
      // Remove the role from cache
      queryClient.removeQueries({ 
        queryKey: divisionRoleQueryKeys.detail(divisionId, roleId) 
      });
    },
  });
}
